import React from 'react'
import { useTranslation } from 'react-i18next'
import { StudentInfo } from '../../../../hooks/useBreakdownDataLicensingFee'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { LedgerCell, LedgerRow } from '../../../Table/LedgerTable'
import { BreakdownForPDF } from './BreakdownForPDF'
import { borderDashed, fontFamily, styles } from './licensingBillingPdfStyles'
import { LicensingPayment } from '../../../../swagger'
import { dateToDashString } from '../../../../utils/dateUtility'
import { TFunction } from 'i18next'
import { colors } from '../../../../stylesheet'

export enum BreakDownLedgerPDFVariants {
  Balance = 'Balance',
  Normal = 'Normal',
}

export interface NormalVariantProps {
  variant: BreakDownLedgerPDFVariants.Normal
  firstStudent: StudentInfo
  additionalStudent: StudentInfo
  subtotal: number
  headerLabel: string
}

export interface BalanceVariantProps {
  variant: BreakDownLedgerPDFVariants.Balance
  totalOwed: number
  totalPaid: number
  balance: number
  currencyCode: string
  programName: string
  paymentList: LicensingPayment[]
  licensingDue: number
}

interface BreakdownLedgerRowForPDF {
  content?: string
  cssTextProps?: React.CSSProperties
}

interface BuildBreakdownLedgerRowsProps {
  elements: Array<BreakdownLedgerRowForPDF>
  cellsCount?: number
}

const buildBreakdownLedgerRows = ({
  elements,
  cellsCount = 4,
}: BuildBreakdownLedgerRowsProps): LedgerRow => {
  while (elements.length !== cellsCount) {
    elements.push({})
  }

  const cells: LedgerCell[] = []

  for (let i = 0; i < cellsCount; i++) {
    const { content, cssTextProps } = elements[i] ?? {
      content: '',
      cssTextProps: undefined,
    }
    cells.push({
      content: content ?? '',
      align: i === 0 ? 'left' : 'right',
      cssTextProps,
    })
  }

  return { cells }
}

const createPaymentsList = (paymentList: LicensingPayment[], t: TFunction) => [
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t(
          'BreakdownLedger.PDF.Balance.SubHeader.Payments',
          'Payments'
        ),
        cssTextProps: {
          borderTop: borderDashed,
          color: colors.royalBlue.main,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
    ],
    cellsCount: 5,
  }),
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Date', 'Date'),
        cssTextProps: styles.paymentListHeader,
      },
      {
        content: t(
          'BreakdownLedger.PDF.Balance.SubHeader.Description',
          'Description'
        ),
        cssTextProps: styles.paymentListHeader,
      },
      {
        content: t(
          'BreakdownLedger.PDF.Balance.SubHeader.PaymentMethod',
          'Payment Method'
        ),
        cssTextProps: styles.paymentListHeader,
      },
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Status', 'Status'),
        cssTextProps: styles.paymentListHeader,
      },
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Amount', 'Amount'),
        cssTextProps: styles.paymentListHeader,
      },
    ],
    cellsCount: 5,
  }),
  ...paymentList.map((payment) =>
    buildBreakdownLedgerRows({
      elements: [
        {
          content: dateToDashString(payment.begunAt),
          cssTextProps: {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.desc,
          cssTextProps: {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.paymentMethodKey,
          cssTextProps: {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.paymentStatusKey,
          cssTextProps: {
            borderBottom: borderDashed,
          },
        },
        {
          content: `$ ${getLocaleCurrencyForAmount(
            payment.amount.amount,
            payment.amount.currencyCode
          )}`,
          cssTextProps: {
            borderBottom: borderDashed,
          },
        },
      ],
      cellsCount: 5,
    })
  ),
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t('BreakdownLedger.PDF.Balance.Row.SubTotal', 'Sub Total'),
        cssTextProps: {
          borderBottom: borderDashed,
        },
      },
      {},
      {},
      {},
      {
        content: `${getLocaleCurrencyForAmount(
          paymentList.reduce((acc, cur) => acc + cur.amount.amount, 0),
          paymentList[0].amount.currencyCode
        )}`,
        cssTextProps: {
          borderBottom: borderDashed,
        },
      },
    ],
    cellsCount: 5,
  }),
]

export const BreakdownLedgerForPDF: React.FC<
  NormalVariantProps | BalanceVariantProps
> = (props) => {
  const { t } = useTranslation()

  let rows: LedgerRow[] = []
  let label = ''

  if (props.variant === BreakDownLedgerPDFVariants.Balance) {
    rows = [
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Balance.Row.TotalOwed',
              'Total Owed'
            ),
          },
          {},
          {},
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.totalOwed,
              props.currencyCode
            )}`,
          },
        ],
      }),
      ...(props.paymentList.length > 0
        ? createPaymentsList(props.paymentList, t)
        : []),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Balance.Row.TotalPaid',
              'Total Paid'
            ),
          },
          {},
          {},
          {
            content: `${
              props.totalPaid > 0 ? '-' : ''
            } $ ${getLocaleCurrencyForAmount(
              props.totalPaid,
              props.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Balance.Row.Balance',
              'Balance for {{program}}',
              {
                program: props.programName,
              }
            ),
            cssTextProps: {
              borderTop: borderDashed,
              fontWeight: 'bold',
              fontFamily: fontFamily,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.balance,
              props.currencyCode
            )}`,
            cssTextProps: {
              fontWeight: 'bold',
              fontFamily: fontFamily,
              borderTop: borderDashed,
            },
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Balance.Row.LicensingDue',
              'Licensing Due'
            ),
            cssTextProps: {
              borderTop: borderDashed,
              fontWeight: 'bold',
              fontFamily: fontFamily,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.licensingDue,
              props.currencyCode
            )}`,
            cssTextProps: {
              fontWeight: 'bold',
              fontFamily: fontFamily,
              borderTop: borderDashed,
            },
          },
        ],
      }),
    ]
  } else {
    rows = [
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Normal.Row.FirstStudents',
              'First Students'
            ),
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.firstStudent.feeAmount,
              props.firstStudent.currencyCode
            )}`,
          },
          { content: `${props.firstStudent.numberOfStudents}` },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.firstStudent.studentInfoSubTotal,
              props.firstStudent.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Normal.Row.AdditionalStudents',
              'Additional Students'
            ),
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.additionalStudent.feeAmount,
              props.additionalStudent.currencyCode
            )}`,
          },
          { content: `${props.additionalStudent.numberOfStudents}` },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.additionalStudent.studentInfoSubTotal,
              props.additionalStudent.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {},
          {},
          {},
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.subtotal,
              props.firstStudent.currencyCode
            )}`,
            cssTextProps: {
              fontWeight: 'bold',
              fontFamily: fontFamily,
              borderTop: borderDashed,
            },
          },
        ],
      }),
    ]
    label = props.headerLabel
  }

  return <BreakdownForPDF breakdownRows={rows} headerLabel={label} />
}
